import {
	Box,
	Button,
	Card,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { useState } from "react";
import { createFrontendTrpcClient, validateProfileField } from "@/frontend/utils";

const trpcClient = createFrontendTrpcClient();

interface ProfileCompletionCardProps {
	user: {
		id: string;
		displayname: string;
		firstname: string;
		lastname: string;
		mobile: string | null;
		email: string;
	};
	setErrorMsg: (msg: string | null) => void;
	setSuccessMsg: (msg: string | null) => void;
}

const ProfileCompletionCard: React.FC<ProfileCompletionCardProps> = ({
	user,
	setErrorMsg,
	setSuccessMsg,
}) => {
	const qc = useQueryClient();

	const [displayname, setDisplayname] = useState(user.displayname || "");
	const [firstname, setFirstname] = useState(user.firstname || "");
	const [lastname, setLastname] = useState(user.lastname || "");
	const [mobile, setMobile] = useState(user.mobile || "");

	const [displaynameError, setDisplaynameError] = useState<string | null>(null);
	const [firstnameError, setFirstnameError] = useState<string | null>(null);
	const [lastnameError, setLastnameError] = useState<string | null>(null);
	const [mobileError, setMobileError] = useState<string | null>(null);

	const updateProfileMutation = useMutation({
		mutationFn: async (data: {
			displayname: string;
			firstname: string;
			lastname: string;
			mobile: string;
		}) => trpcClient.user.updateProfile.mutate(data),
		onSuccess: () => {
			setSuccessMsg("Profil erfolgreich aktualisiert!");
			qc.invalidateQueries({ queryKey: ["me"] });
		},
		onError: (err: any) => {
			setErrorMsg(err?.message ?? "Fehler beim Aktualisieren des Profils");
		},
	});

	const handleSubmit = async () => {
		// Validate
		let hasError = false;
		const displaynameErr = validateProfileField(displayname, "Anzeigename");
		if (displaynameErr) {
			setDisplaynameError(displaynameErr);
			hasError = true;
		} else {
			setDisplaynameError(null);
		}
		const firstnameErr = validateProfileField(firstname, "Vorname");
		if (firstnameErr) {
			setFirstnameError(firstnameErr);
			hasError = true;
		} else {
			setFirstnameError(null);
		}
		const lastnameErr = validateProfileField(lastname, "Nachname");
		if (lastnameErr) {
			setLastnameError(lastnameErr);
			hasError = true;
		} else {
			setLastnameError(null);
		}
		const mobileErr = validateProfileField(mobile, "Handynummer", 8);
		if (mobileErr) {
			setMobileError(mobileErr);
			hasError = true;
		} else {
			setMobileError(null);
		}
		if (hasError) return;

		await updateProfileMutation.mutateAsync({
			displayname: displayname.trim(),
			firstname: firstname.trim(),
			lastname: lastname.trim(),
			mobile: mobile.trim(),
		});
	};

	return (
		<Card variant="elevation" sx={{ bgcolor: "rgba(255,255,255,0.7)" }}>
			<Box sx={{ p: 2 }}>
				<Typography variant="h6" gutterBottom>
					Danke! Du bist eingetragen.
				</Typography>
				<Typography variant="body2" sx={{ mb: 2 }}>
					Bitte fülle das hier noch aus. Andere Teilnehmer können nur deinen
					Anzeigennamen sehen. Die Infos helfen mir eingegangene
					PayPal-Zahlungen zu matchen, bzw. dich in der WhatsApp-Gruppe
					zuzuordnen.
				</Typography>
				<Stack spacing={2}>
					<TextField
						label="Anzeigename"
						fullWidth
						value={displayname}
						onChange={(e) => setDisplayname(e.target.value)}
						error={!!displaynameError}
						helperText={displaynameError}
						inputProps={{ "data-testid": "profile-displayname" }}
					/>
					<TextField
						label="Vorname"
						fullWidth
						value={firstname}
						onChange={(e) => setFirstname(e.target.value)}
						error={!!firstnameError}
						helperText={firstnameError}
						inputProps={{ "data-testid": "profile-firstname" }}
					/>
					<TextField
						label="Nachname"
						fullWidth
						value={lastname}
						onChange={(e) => setLastname(e.target.value)}
						error={!!lastnameError}
						helperText={lastnameError}
						inputProps={{ "data-testid": "profile-lastname" }}
					/>
					<TextField
						label="Handynummer"
						fullWidth
						value={mobile}
						onChange={(e) => setMobile(e.target.value)}
						error={!!mobileError}
						helperText={mobileError}
						inputProps={{ "data-testid": "profile-mobile" }}
					/>
				</Stack>
				<Box
					sx={{
						mt: 2,
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<Button
						variant="contained"
						onClick={handleSubmit}
						disabled={updateProfileMutation.isPending}
						data-testid="profile-save"
					>
						Speichern
					</Button>
				</Box>
			</Box>
		</Card>
	);
};

export default ProfileCompletionCard;
